import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { IconType } from 'react-icons';
import { FiAlertTriangle, FiCheckCircle, FiClock, FiInfo, FiLoader, FiXCircle } from 'react-icons/fi';
import { useLanguage } from './Header';
import { cancelReservation } from '../services/reservationService';

// Contact address for error messages; the translations contain an {email} placeholder
const CONTACT_EMAIL = 'info@phonebros.be';

interface CancelReservationPageProps {
  orderNumber: string;
  token: string | null;
  onDone: () => void;
}

type Step = 'confirm' | 'loading' | 'done';
type Tone = 'success' | 'info' | 'warning' | 'error';

interface Outcome {
  tone: Tone;
  icon: IconType;
  titleKey: string;
  textKey: string;
}

/**
 * Maps a cancel-reservation result code to icon, colour and translation keys.
 * Unknown codes (invalid_request, unexpected values) fall back to the generic error.
 */
const getOutcome = (code: string): Outcome => {
  switch (code) {
    case 'cancelled':
      return { tone: 'success', icon: FiCheckCircle, titleKey: 'cancelSuccessTitle', textKey: 'cancelSuccessText' };
    case 'already_cancelled':
      return { tone: 'info', icon: FiInfo, titleKey: 'cancelAlreadyTitle', textKey: 'cancelAlreadyText' };
    case 'too_late':
      return { tone: 'warning', icon: FiClock, titleKey: 'cancelTooLateTitle', textKey: 'cancelTooLateText' };
    case 'invalid_token':
    case 'not_found':
      return { tone: 'error', icon: FiXCircle, titleKey: 'cancelInvalidTitle', textKey: 'cancelInvalidText' };
    case 'rate_limited':
      return { tone: 'warning', icon: FiAlertTriangle, titleKey: 'cancelRateLimitedTitle', textKey: 'cancelRateLimitedText' };
    default:
      return { tone: 'error', icon: FiAlertTriangle, titleKey: 'cancelErrorTitle', textKey: 'cancelErrorText' };
  }
};

const toneColors: Record<Tone, string> = {
  success: '#10b981',
  info: '#3b82f6',
  warning: '#f59e0b',
  error: '#ef4444',
};

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Page = styled.div`
  max-width: 560px;
  margin: 0 auto;
  padding: clamp(7rem, 8vw, 8.5rem) 1.5rem 3rem;
  min-height: 100vh;
  position: relative;
  z-index: 10;

  @media (max-width: 768px) {
    padding-top: 6.5rem;
  }

  @media (max-width: 480px) {
    padding: 5.5rem 1rem 2rem;
  }
`;

const Card = styled.div`
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
  border: 1px solid #e8eef5;
  border-radius: 28px;
  box-shadow: 0 12px 30px rgba(2, 6, 23, 0.06);
  padding: 2.5rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
  text-align: center;

  @media (max-width: 480px) {
    padding: 1.75rem 1.25rem;
    border-radius: 20px;
    gap: 1rem;
  }

  .dark & {
    background: linear-gradient(180deg, #0f1116 0%, #0c1118 100%);
    border-color: #253042;
    box-shadow: 0 14px 32px rgba(0, 0, 0, 0.5);
  }
`;

const StatusIcon = styled.div<{ $tone: Tone }>`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: ${({ $tone }) => toneColors[$tone]};
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);

  svg {
    width: 36px;
    height: 36px;
  }

  @media (max-width: 480px) {
    width: 64px;
    height: 64px;

    svg {
      width: 32px;
      height: 32px;
    }
  }
`;

const Spinner = styled.div`
  display: flex;
  color: #0f172a;
  animation: ${spin} 1s linear infinite;

  svg {
    width: 36px;
    height: 36px;
  }

  .dark & {
    color: #f1f5f9;
  }
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  line-height: 1.3;
  letter-spacing: -0.01em;

  .dark & {
    color: #f1f5f9;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const Message = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #475569;
  margin: 0;
  max-width: 420px;

  .dark & {
    color: #9ca3af;
  }
`;

const Question = styled(Message)`
  font-weight: 600;
  color: #0f172a;

  .dark & {
    color: #f1f5f9;
  }
`;

const OrderBox = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 0.75rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  max-width: 100%;

  .dark & {
    background: #111827;
    border-color: #253042;
  }
`;

const OrderLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
`;

const OrderValue = styled.span`
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
  word-break: break-all;

  .dark & {
    color: #f1f5f9;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
  width: 100%;
  margin-top: 0.5rem;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const PrimaryButton = styled.button`
  background: #0f172a;
  color: #ffffff;
  border: 1px solid #0f172a;
  border-radius: 12px;
  padding: 0.9rem 1.5rem;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.15);
  letter-spacing: -0.01em;

  &:hover:not(:disabled) {
    background: #1e293b;
    border-color: #1e293b;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.2);
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }

  .dark & {
    background: #f1f5f9;
    color: #0f172a;
    border-color: #f1f5f9;
  }

  @media (max-width: 480px) {
    width: 100%;
    min-height: 44px;
    font-size: 0.9375rem;
    padding: 0.75rem 1.25rem;

    &:hover:not(:disabled) {
      transform: none;
    }
  }
`;

const SecondaryButton = styled.button`
  background: #ffffff;
  color: #374151;
  border: 1px solid rgba(17, 24, 39, 0.12);
  border-radius: 12px;
  padding: 0.9rem 1.5rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
    border-color: rgba(17, 24, 39, 0.18);
  }

  .dark & {
    background: transparent;
    color: #cbd5e1;
    border-color: #334155;
  }

  @media (max-width: 480px) {
    width: 100%;
    min-height: 44px;
    font-size: 0.9375rem;
    padding: 0.75rem 1.25rem;
  }
`;

/**
 * Landing page for the cancel link in the confirmation email.
 * The cancellation is only sent after the customer clicks the confirm button,
 * so opening the link (or a mail scanner following it) never cancels anything.
 */
const CancelReservationPage: React.FC<CancelReservationPageProps> = ({ orderNumber, token, onDone }) => {
  const { language, t, isLoading } = useLanguage();
  const [step, setStep] = useState<Step>('confirm');
  const [resultCode, setResultCode] = useState('');

  // Translations contain an {email} placeholder for the contact address
  const text = (key: string): string => t(key).replace('{email}', CONTACT_EMAIL);

  const handleConfirm = async () => {
    if (!token || !orderNumber || step === 'loading') return;

    setStep('loading');
    const result = await cancelReservation(orderNumber, token, language);
    setResultCode(result.code);
    setStep('done');
  };

  // Wait for the translations of the link language, so no raw keys are shown
  if (isLoading) {
    return null;
  }

  // Old emails have no token, and a link without order number cannot be used either:
  // only show the contact message
  if (!token || !orderNumber) {
    return (
      <Page>
        <Card>
          <StatusIcon $tone="warning" aria-hidden="true">
            <FiAlertTriangle />
          </StatusIcon>
          <Message role="alert">{text('cancelNoTokenText')}</Message>
          <PrimaryButton type="button" onClick={onDone}>
            {t('cancelBackHomeButton')}
          </PrimaryButton>
        </Card>
      </Page>
    );
  }

  const orderBox = (
    <OrderBox>
      <OrderLabel>{t('cancelOrderNumberLabel')}</OrderLabel>
      <OrderValue>{orderNumber}</OrderValue>
    </OrderBox>
  );

  if (step === 'done') {
    const outcome = getOutcome(resultCode);
    const ResultIcon = outcome.icon;

    return (
      <Page>
        <Card>
          <StatusIcon $tone={outcome.tone} aria-hidden="true">
            <ResultIcon />
          </StatusIcon>
          <Title>{t(outcome.titleKey)}</Title>
          <Message role="status" aria-live="polite">{text(outcome.textKey)}</Message>
          {orderBox}
          <PrimaryButton type="button" onClick={onDone}>
            {t('cancelBackHomeButton')}
          </PrimaryButton>
        </Card>
      </Page>
    );
  }

  return (
    <Page>
      <Card>
        <Title>{t('cancelPageTitle')}</Title>
        {orderBox}
        {step === 'loading' ? (
          <>
            <Spinner aria-hidden="true">
              <FiLoader />
            </Spinner>
            <Message role="status" aria-live="polite">{t('cancelLoadingText')}</Message>
          </>
        ) : (
          <>
            <Question>{t('cancelConfirmQuestion')}</Question>
            <ButtonRow>
              <PrimaryButton type="button" onClick={handleConfirm}>
                {t('cancelConfirmButton')}
              </PrimaryButton>
              <SecondaryButton type="button" onClick={onDone}>
                {t('cancelDeclineButton')}
              </SecondaryButton>
            </ButtonRow>
          </>
        )}
      </Card>
    </Page>
  );
};

export default CancelReservationPage;
